"use client";

import { useState } from "react";
import { DurationValueSchema, type DurationValue } from "@repo/questionnaire";
import {
  spanishChromeLabel,
  translationSourceHash,
} from "@repo/questionnaire";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const CONTROL_CLASS = "h-12 text-base md:text-base";

export function DurationField({
  id,
  value,
  disabled,
  locale = "en",
  translations = {},
  onChange,
}: {
  id: string;
  value: unknown;
  disabled?: boolean;
  locale?: string;
  translations?: Record<string, string>;
  onChange: (value: DurationValue | "") => void;
}) {
  const parsed = DurationValueSchema.safeParse(value);
  const [unit, setUnit] = useState<DurationValue["unit"]>(
    parsed.success ? parsed.data.unit : "years",
  );
  const amount = parsed.success ? String(parsed.data.amount) : "";
  const translate = (text: string) =>
    locale === "en"
      ? text
      : translations[translationSourceHash(text)] ??
        (locale === "es" ? spanishChromeLabel(text) : text);

  function commit(nextAmount: string, nextUnit: DurationValue["unit"]) {
    setUnit(nextUnit);
    const trimmed = nextAmount.trim();
    if (!trimmed) {
      onChange("");
      return;
    }
    const numeric = Number(trimmed);
    if (!Number.isInteger(numeric) || numeric < 0) return;
    onChange({ amount: numeric, unit: nextUnit });
  }

  return (
    <div className="flex flex-col gap-3">
      <fieldset className="grid grid-cols-2 gap-2 border-0 p-0">
        <legend className="sr-only">{translate("Years or months")}</legend>
        {(["years", "months"] as const).map((option) => {
          const selected = unit === option;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              className={cn(
                "h-12 rounded-md border text-base font-medium capitalize",
                selected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background text-foreground",
              )}
              onClick={() => commit(amount, option)}
            >
              {translate(option === "years" ? "Years" : "Months")}
            </button>
          );
        })}
      </fieldset>
      <Input
        id={id}
        inputMode="numeric"
        pattern="[0-9]*"
        disabled={disabled}
        value={amount}
        placeholder={translate(
          unit === "years" ? "Number of years" : "Number of months",
        )}
        className={CONTROL_CLASS}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, "").slice(0, 3);
          commit(digits, unit);
        }}
      />
    </div>
  );
}
